/**
 * A request to queue a dispatch whose outcome is unknown (batch 8 T12c1, #3033).
 *
 * The exact request (channel, document version, its own key) is held for its owner across a closed dialog, a remount and a page reload.
 * Nothing is sent again on its own: the user may send the very same request again (the server recognises it, so it can never create a
 * second dispatch), look at the list, or give it up knowingly. Giving it up never hides a dispatch that may already exist.
 */

import { useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import type { EnqueueCommand, EnqueueResult } from '@/hooks/useProcurementDispatch';
import { describeDispatchError } from './dispatchErrors';
import { formatDateTimeZone } from './dispatchFormat';

interface PendingEnqueueNoticeProps {
  enqueue: EnqueueCommand;
  /** Reloads the list so the user can see whether the dispatch exists. */
  onCheckList?: (() => void) | undefined;
  /** The retry reached a conclusion on the server. */
  onResult?: ((result: EnqueueResult) => void) | undefined;
}

export function PendingEnqueueNotice({
  enqueue,
  onCheckList,
  onResult,
}: PendingEnqueueNoticeProps) {
  const { t, i18n } = useTranslation('procurementDispatch');
  const { t: tc } = useTranslation('common');
  const [discarding, setDiscarding] = useState(false);
  const [lastResult, setLastResult] = useState<EnqueueResult | null>(null);
  const { held, journalStatus } = enqueue;

  const retry = async () => {
    const result = await enqueue.retry();
    setLastResult(result);
    onResult?.(result);
  };

  return (
    <Stack spacing={1.5}>
      {journalStatus === 'unavailable' && (
        <Alert severity="warning">{t('pending.storageUnavailable')}</Alert>
      )}
      {journalStatus === 'corrupt' && (
        <Alert
          severity="error"
          role="alert"
          action={
            <Button color="inherit" size="small" onClick={enqueue.discardDamaged}>
              {t('pending.discardDamaged')}
            </Button>
          }
        >
          {t('pending.damaged')}
        </Alert>
      )}

      {held !== null && (
        <Alert severity="warning" role="alert" aria-label={t('pending.title')}>
          <Typography variant="body2" fontWeight={600}>
            {t('pending.title')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {t('pending.body', {
              channel: t(`channel.${held.body.channel}`),
              version: held.documentVersion,
              time: formatDateTimeZone(held.startedAt, i18n.language),
              attempts: held.attempts,
            })}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {t('pending.noAutoResend')}
          </Typography>
          {lastResult !== null && lastResult.status === 'uncertain' && (
            <Typography variant="body2" sx={{ mt: 0.5 }} role="status">
              {t('pending.stillUnknown')} {describeDispatchError(t, lastResult.error)}
            </Typography>
          )}
          {lastResult !== null && lastResult.status === 'refused' && (
            <Typography variant="body2" sx={{ mt: 0.5 }} role="status">
              {describeDispatchError(t, lastResult.error)}
            </Typography>
          )}
          {lastResult !== null && lastResult.status === 'not_durable' && (
            <Typography variant="body2" sx={{ mt: 0.5 }} role="status">
              {t('pending.notStored')}
            </Typography>
          )}
          {lastResult !== null && lastResult.status === 'canceled' && (
            <Typography variant="body2" sx={{ mt: 0.5 }} role="status">
              {t('pending.notSent')}
            </Typography>
          )}
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => void retry()}
              disabled={enqueue.isPending}
            >
              {enqueue.isPending ? t('pending.retrying') : t('pending.retry')}
            </Button>
            {onCheckList && (
              <Button size="small" onClick={onCheckList} disabled={enqueue.isPending}>
                {t('pending.checkList')}
              </Button>
            )}
            <Button
              size="small"
              color="inherit"
              onClick={() => setDiscarding(true)}
              disabled={enqueue.isPending}
            >
              {t('pending.discard')}
            </Button>
          </Box>
        </Alert>
      )}

      <ConfirmDialog
        open={discarding}
        title={t('pending.discardTitle')}
        message={t('pending.discardWarning')}
        confirmText={t('pending.discardConfirm')}
        cancelText={tc('cancel')}
        confirmColor="warning"
        onCancel={() => setDiscarding(false)}
        onConfirm={() => {
          enqueue.discard();
          setDiscarding(false);
        }}
      />
    </Stack>
  );
}
